/**
 * UploadOverviewTab.js
 * State-of-the-art upload and overview interface with modern responsive design
 */

import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import {
  Analytics as StatsIcon,
  CloudUpload as CloudIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';

import FileUploadSection from './FileUploadSection';
import StatementOverview from './StatementOverview';
import ReconciliationStats from './ReconciliationStats';

const UploadOverviewTab = ({
  statements,
  stats,
  selectedStatement,
  isLoadingStatements,
  isLoadingStats,
  isUploading,
  uploadProgress,
  onFileUpload,
  onStatementSelect,
  onStatementDelete,
  onStatementBulkUnreconcile,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Safely get statements count regardless of data structure
  const getStatementsCount = () => {
    if (Array.isArray(statements)) {
      return statements.length;
    } else if (statements && typeof statements === 'object' && Array.isArray(statements.data)) {
      return statements.data.length;
    }
    return 0;
  };

  const statementsCount = getStatementsCount();

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: alpha(theme.palette.grey[300], 0.3),
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.grey[500], 0.5),
          borderRadius: '3px',
          '&:hover': {
            background: alpha(theme.palette.grey[600], 0.7),
          },
        },
      }}
    >
      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, sm: 3, md: 4 } }}>
        <Grid
          container
          spacing={{ xs: 1, md: 2 }}
          sx={{ minHeight: 'calc(100vh - 500px)', alignItems: 'stretch' }}
        >
          {/* Upload Section - Full Width on Mobile, Half on Desktop */}
          <Grid item xs={12} lg={6} sx={{ flex: 1 }}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.background.paper,
                  0.9
                )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    background: alpha(theme.palette.common.white, 0.2),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CloudIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </Box>
                <Box>
                  <Typography
                    variant={isMobile ? 'subtitle1' : 'h6'}
                    fontWeight="700"
                    sx={{ lineHeight: 1.2 }}
                  >
                    Upload de Extrato
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                    Envie arquivos OFX para conciliação
                  </Typography>
                </Box>
              </Box>
              <CardContent
                sx={{
                  p: { xs: 2, sm: 3 },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <FileUploadSection
                  onFileUpload={onFileUpload}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Statements Overview - Full Width on Mobile, Half on Desktop */}
          <Grid item xs={12} lg={6} sx={{ flex: 1 }}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.background.paper,
                  0.9
                )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  boxShadow: `0 8px 32px ${alpha(theme.palette.secondary.main, 0.1)}`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      background: alpha(theme.palette.common.white, 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DocumentIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </Box>
                  <Box>
                    <Typography
                      variant={isMobile ? 'subtitle1' : 'h6'}
                      fontWeight="700"
                      sx={{ lineHeight: 1.2 }}
                    >
                      Extratos Carregados
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                      Gerencie seus arquivos processados
                    </Typography>
                  </Box>
                </Box>
                {statementsCount > 0 && (
                  <Chip
                    label={statementsCount}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: alpha(theme.palette.common.white, 0.2),
                      color: 'white',
                      borderRadius: 2,
                      px: 1,
                    }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  maxHeight: 'calc(100vh - 280px)',
                }}
              >
                <StatementOverview
                  statements={statements}
                  isLoading={isLoadingStatements}
                  onStatementSelect={onStatementSelect}
                  onStatementDelete={onStatementDelete}
                  onStatementBulkUnreconcile={onStatementBulkUnreconcile}
                  selectedStatement={selectedStatement}
                />
              </Box>
            </Card>
          </Grid>

          {/* Statistics Section - Full Width */}
          {/* <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.background.paper,
                  0.9
                )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.1)}`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    background: alpha(theme.palette.common.white, 0.2),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <StatsIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </Box>
                <Box>
                  <Typography
                    variant={isMobile ? 'subtitle1' : 'h6'}
                    fontWeight="700"
                    sx={{ lineHeight: 1.2 }}
                  >
                    Estatísticas de Conciliação
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                    Acompanhe o progresso das suas conciliações
                  </Typography>
                </Box>
              </Box>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <ReconciliationStats stats={stats} isLoading={isLoadingStats} />
              </CardContent>
            </Card>
          </Grid> */}
        </Grid>
      </Box>
    </Box>
  );
};

export default UploadOverviewTab;
